
import { useState, useEffect, useMemo } from 'react';
import { useQuery } from '@tanstack/react-query';
import { Plan, FilterOptions, SortOption } from '../types';
import { fetchFilteredPlans } from '../services/planService';
import { useToast } from "@/hooks/use-toast";

export function usePlans() {
  const { toast } = useToast();
  const [filters, setFilters] = useState<FilterOptions>({
    providers: [],
    dataAllowances: [],
    contractLengths: [],
    talkTimes: [],
    roaming: [],
    minPrice: 0,
    maxPrice: 500
  });
  
  const [sortBy, setSortBy] = useState<SortOption>('relevance');
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedPlans, setSelectedPlans] = useState<string[]>([]);
  const [forceRefresh, setForceRefresh] = useState<number>(0);
  
  // Handle search query preprocessing
  const processedSearchQuery = useMemo(() => {
    const lowerQuery = searchQuery.toLowerCase();
    // Handle special case for "fri data" or similar searches
    if ((lowerQuery.includes("fri") && lowerQuery.includes("data")) || lowerQuery === "fri") {
      return "Ubegrænset";
    }
    return searchQuery;
  }, [searchQuery]);
  
  // Helper function to sort data allowances with "Ubegrænset" first and the rest in descending order
  const sortDataAllowances = (dataAllowances: string[]): string[] => {
    return dataAllowances.sort((a, b) => {
      if (a === 'Ubegrænset') return -1;
      if (b === 'Ubegrænset') return 1;
      
      // Extract values for comparison (remove GB, MB, etc.)
      const valueA = parseInt(a.replace(/[^0-9]/g, '')) || 0;
      const valueB = parseInt(b.replace(/[^0-9]/g, '')) || 0;
      
      return valueB - valueA; // Descending order
    });
  };
  
  // Fetch plans from Supabase
  const { data: plansData = [], isLoading, error, refetch } = useQuery({
    queryKey: ['plans', filters, sortBy, processedSearchQuery, forceRefresh],
    queryFn: () => fetchFilteredPlans(filters, sortBy, processedSearchQuery),
    staleTime: 60000, // Consider data stale after 1 minute
    meta: {
      onError: (error: Error) => {
        console.error('Error fetching plans:', error);
        toast({
          title: "Fejl ved hentning af abonnementer",
          description: "Der opstod en fejl ved hentning af abonnementer.",
          variant: "destructive",
        });
      }
    }
  });
  
  // Filter plans by price range client-side to ensure max price is respected
  const filteredPlans = useMemo(() => {
    return plansData.filter(plan => {
      // Apply price filters if they exist
      if (filters.minPrice !== undefined && plan.price < filters.minPrice) {
        return false;
      }
      if (filters.maxPrice !== undefined && plan.price > filters.maxPrice) {
        return false;
      }
      return true;
    });
  }, [plansData, filters.minPrice, filters.maxPrice]);
  
  // Show error toast when an error occurs
  useEffect(() => {
    if (error) {
      console.error('Error fetching plans:', error);
      toast({
        title: "Fejl ved hentning af abonnementer",
        description: "Der opstod en fejl ved hentning af abonnementer.",
        variant: "destructive",
      });
    }
  }, [error, toast]);
  
  // Get the selected plans for comparison
  const plansToCompare = useMemo(() => {
    return filteredPlans.filter(plan => selectedPlans.includes(plan.id));
  }, [filteredPlans, selectedPlans]);
  
  // Toggle a plan for comparison
  const togglePlanSelection = (planId: string) => {
    setSelectedPlans(prev => {
      if (prev.includes(planId)) {
        return prev.filter(id => id !== planId);
      } else {
        // Only allow up to 3 plans for comparison
        const newSelection = [...prev, planId].slice(-3);
        return newSelection;
      }
    });
  };
  
  // Reset filters
  const resetFilters = () => {
    setFilters({
      providers: [],
      dataAllowances: [],
      contractLengths: [],
      talkTimes: [],
      roaming: [],
      minPrice: 0,
      maxPrice: 500
    });
    setSortBy('relevance');
    setSearchQuery('');
  };
  
  // Trigger a refresh of the plans data
  const refreshPlans = async () => {
    console.log("Refreshing plans data...");
    setForceRefresh(prev => prev + 1);
    const result = await refetch();
    console.log("Plans refresh complete:", result.isSuccess ? "success" : "failed");
    return result;
  };
  
  return {
    allPlans: plansData, // Return the raw plansData for use in filters
    filteredPlans,
    filters,
    setFilters,
    sortBy,
    setSortBy,
    searchQuery,
    setSearchQuery,
    selectedPlans,
    plansToCompare,
    togglePlanSelection,
    resetFilters,
    refreshPlans,
    isLoading,
    error,
    sortDataAllowances, // Export the sorting function for use in filter components
  };
}
